import { memo, useEffect, useMemo, useRef, useState } from "react";

interface StickerSetView {
  id: string;
  title: string;
  kind: "installed" | "favorite" | "recent";
  stickers: Array<{
    fileId: string;
    emoji?: string;
    thumbUrl?: string;
    width?: number;
    height?: number;
  }>;
}

interface ComposerPickerProps {
  open: boolean;
  onClose(): void;
  onEmoji(value: string): void;
  onSticker(sticker: {
    fileId: string;
    emoji?: string;
    width?: number;
    height?: number;
  }): void;
  stickerSets: StickerSetView[];
  stickersLoading: boolean;
  requestStickerSets(): void;
}

// Минимальный набор популярных emoji, разделённый по категориям. Сделано
// без зависимости на emoji-mart (тяжёлый bundle) — для саппорта/менеджера
// этого хватает. Кому надо больше — копи-паст с панели OS работает как раньше.
const EMOJI_CATEGORIES: Array<{ label: string; items: string[] }> = [
  {
    label: "Часто",
    items: [
      "👍",
      "❤️",
      "🔥",
      "😂",
      "😍",
      "🙏",
      "👏",
      "💯",
      "🎉",
      "😎",
      "😭",
      "😅",
      "🤔",
      "👌",
      "✅",
      "❗",
      "🙌",
      "🤝",
      "💪",
      "🥰",
      "😊",
      "🤗",
      "🤣",
      "😉"
    ]
  },
  {
    label: "Смайлы",
    items: [
      "😀",
      "😃",
      "😄",
      "😁",
      "😆",
      "🥹",
      "🥲",
      "😋",
      "😌",
      "🙃",
      "😏",
      "😒",
      "😞",
      "😔",
      "😟",
      "😕",
      "🙁",
      "☹️",
      "😣",
      "😖",
      "😫",
      "😩",
      "😢",
      "😤",
      "😠",
      "😡",
      "🤬",
      "🤯",
      "😳",
      "🥵",
      "🥶",
      "😱",
      "😨",
      "😰",
      "😥",
      "😓"
    ]
  },
  {
    label: "Жесты",
    items: [
      "👋",
      "🤚",
      "🖐️",
      "✋",
      "🖖",
      "👌",
      "🤌",
      "🤏",
      "✌️",
      "🤞",
      "🤟",
      "🤘",
      "🤙",
      "👈",
      "👉",
      "👆",
      "👇",
      "☝️",
      "👍",
      "👎",
      "✊",
      "👊",
      "🤛",
      "🤜",
      "👏",
      "🙌",
      "👐",
      "🤲",
      "🤝",
      "🙏"
    ]
  },
  {
    label: "Предметы",
    items: [
      "📱",
      "💻",
      "🖥️",
      "⌨️",
      "🖱️",
      "💾",
      "💿",
      "📷",
      "🎥",
      "🎬",
      "📺",
      "📻",
      "🎙️",
      "🎚️",
      "🎛️",
      "⏱️",
      "⏳",
      "🔋",
      "🔌",
      "💡",
      "🔦",
      "🕯️",
      "💰",
      "💳",
      "💎",
      "⚖️",
      "🔧",
      "🔨",
      "⚒️",
      "🛠️"
    ]
  },
  {
    label: "Символы",
    items: [
      "❤️",
      "🧡",
      "💛",
      "💚",
      "💙",
      "💜",
      "🖤",
      "🤍",
      "🤎",
      "💔",
      "❣️",
      "💕",
      "💞",
      "💓",
      "💗",
      "💖",
      "💘",
      "💝",
      "✨",
      "🌟",
      "⭐",
      "🌠",
      "☀️",
      "🌈",
      "🔥",
      "💧",
      "🌊",
      "🎶",
      "🎵",
      "✅",
      "❌",
      "⚠️",
      "❓",
      "❗",
      "💯",
      "🆗"
    ]
  }
];

export const ComposerPicker = memo(
  ({
    open,
    onClose,
    onEmoji,
    onSticker,
    stickerSets,
    stickersLoading,
    requestStickerSets
  }: ComposerPickerProps) => {
    const [tab, setTab] = useState<"emoji" | "stickers">("emoji");
    const containerRef = useRef<HTMLDivElement | null>(null);

    // На каждое ОТКРЫТИЕ вкладки «Стикеры» дергаем worker один раз. Worker
    // на каждой загрузке триггерит downloadFile для превью — повторное
    // открытие прогревает thumbnails, которых не было при первом (TDLib
    // докачал в фоне, во второй раз отдаст URL'ы).
    // ВАЖНО: stickersLoading здесь быть не должно ни в депсах, ни в условии —
    // с ним цикл «запрос → loading=true → ответ → loading=false → эффект →
    // запрос» крутился бесконечно, пока открыта вкладка, и душил worker
    // (tdlib сериализует invoke) + ре-рендерил ChatView на каждом ответе.
    useEffect(() => {
      if (open && tab === "stickers") {
        requestStickerSets();
      }
    }, [open, tab, requestStickerSets]);

    // Клик мимо панели = закрыть. Внутри — игнор, иначе вставка эмодзи
    // моментально гасила бы пикер. Esc — тоже закрытие.
    useEffect(() => {
      if (!open) {
        return;
      }
      const onDocClick = (event: MouseEvent): void => {
        if (!containerRef.current) {
          return;
        }
        if (event.target instanceof Node && containerRef.current.contains(event.target)) {
          return;
        }
        onClose();
      };
      const onKey = (event: KeyboardEvent): void => {
        if (event.key === "Escape") {
          onClose();
        }
      };
      window.addEventListener("mousedown", onDocClick);
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("mousedown", onDocClick);
        window.removeEventListener("keydown", onKey);
      };
    }, [open, onClose]);

    const visibleSets = useMemo(() => stickerSets.slice(0, 30), [stickerSets]);

    if (!open) {
      return null;
    }

    return (
      <div ref={containerRef} className="composer-picker" role="dialog" aria-label="Эмодзи и стикеры">
        <div className="composer-picker__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "emoji"}
            className={
              tab === "emoji"
                ? "composer-picker__tab composer-picker__tab--active"
                : "composer-picker__tab"
            }
            onClick={() => setTab("emoji")}
          >
            <i className="ic ic-smile" aria-hidden="true" /> Эмодзи
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "stickers"}
            className={
              tab === "stickers"
                ? "composer-picker__tab composer-picker__tab--active"
                : "composer-picker__tab"
            }
            onClick={() => setTab("stickers")}
          >
            🖼️ Стикеры
          </button>
        </div>
        <div className="composer-picker__body">
          {tab === "emoji" ? (
            <div className="composer-picker__emojis">
              {EMOJI_CATEGORIES.map((category) => (
                <div key={category.label} className="composer-picker__category">
                  <div className="composer-picker__category-title">{category.label}</div>
                  <div className="composer-picker__grid">
                    {category.items.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        className="composer-picker__emoji"
                        onClick={() => onEmoji(emoji)}
                        aria-label={`Вставить ${emoji}`}
                        title={emoji}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="composer-picker__stickers">
              {stickersLoading && visibleSets.length === 0 ? (
                <div className="composer-picker__empty">Загружаем стикеры…</div>
              ) : visibleSets.length === 0 ? (
                <div className="composer-picker__empty">
                  Установленных стикерсетов нет. Добавьте их в основном клиенте Telegram.
                </div>
              ) : (
                visibleSets.map((set) => (
                  <div key={set.id} className="composer-picker__category">
                    <div className="composer-picker__category-title">
                      {set.title}
                      {set.kind === "favorite" ? " ★" : set.kind === "recent" ? " · недавние" : ""}
                    </div>
                    <div className="composer-picker__grid composer-picker__grid--stickers">
                      {set.stickers.slice(0, 60).map((sticker) => (
                        <button
                          key={`${set.id}:${sticker.fileId}`}
                          type="button"
                          className="composer-picker__sticker"
                          onClick={() =>
                            onSticker({
                              fileId: sticker.fileId,
                              ...(sticker.emoji ? { emoji: sticker.emoji } : {}),
                              ...(typeof sticker.width === "number" ? { width: sticker.width } : {}),
                              ...(typeof sticker.height === "number"
                                ? { height: sticker.height }
                                : {})
                            })
                          }
                          aria-label={`Отправить стикер ${sticker.emoji ?? ""}`}
                          title={sticker.emoji ?? set.title}
                        >
                          {sticker.thumbUrl ? (
                            <img
                              src={sticker.thumbUrl}
                              alt={sticker.emoji ?? ""}
                              loading="lazy"
                            />
                          ) : (
                            <span className="composer-picker__sticker-emoji">
                              {sticker.emoji ?? "🖼"}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

ComposerPicker.displayName = "ComposerPicker";
